/**
 * Account repair wording (ACT2-1286 PR 2b, spec "Repair" and UI 10–14). One place per sentence so the
 * banner, the dialog and the item pages say the same thing.
 */
import type { AccountRelinkNotice } from '@server/warehouse-repair-state';

/** What the copy calls the item. A workflow's per-node repair still says "workflow". */
export type RepairItemNoun = 'dashboard' | 'audience' | 'workflow' | 'sketch';

/** The status label on lists and page headers (spec UI 10). */
export const NEEDS_REPAIR_LABEL = 'Needs repair';
export const NEEDS_REPAIR_LABEL_FULL = 'Needs repair: warehouse account removed';
export const UNASSIGNED_LABEL_FULL = 'Needs repair: no warehouse account';
export const UNVERIFIED_LABEL = 'Unverified';

export function removedHeadline(noun: RepairItemNoun): string {
  return `This ${noun}'s warehouse account was removed.`;
}

export function unassignedHeadline(noun: RepairItemNoun): string {
  return `This ${noun} has no warehouse account.`;
}

export function unverifiedHeadline(noun: RepairItemNoun, notice: AccountRelinkNotice | null): string {
  const account = notice ? `account ${notice.displayName}` : 'another account';
  return `This ${noun} now uses ${account}, which could not run all its queries.`;
}

/** Shown in place of Repair to someone who cannot repair the item. */
export const ASK_TO_REPAIR = 'Ask its owner or an admin to repair it.';

/** "Now uses account X (user Y), repaired by Z" — X and Y from the snapshot, so a rename never changes it. */
export function relinkNoticeText(notice: AccountRelinkNotice): string {
  const user = notice.loginUser ? ` (user ${notice.loginUser})` : '';
  return `Now uses account ${notice.displayName}${user}, repaired by ${notice.actorName ?? 'a former member'}.`;
}

export function noAccountCanRun(noun: RepairItemNoun): string {
  return `No account for this database can run this ${noun}. Someone who can manage warehouse accounts can add one in Settings.`;
}

export function noneCouldRunWarning(noun: RepairItemNoun): string {
  return `No account could run all its queries. If you use one anyway, this ${noun} is marked unverified.`;
}

export function pickedFailedWarning(noun: RepairItemNoun): string {
  return `This account could not run all its queries. If you use it, this ${noun} is marked unverified.`;
}

export function cannotRunTooltip(noun: RepairItemNoun): string {
  return `This account cannot run this ${noun}.`;
}

export const REPAIR_CONFLICT = 'Someone else repaired this. Reload.';
/** A routine save refused because a step was repaired after the editor opened (it then reloads). */
export const ROUTINE_REPAIRED_SINCE_LOAD =
  "This routine's warehouse account was repaired after you opened it. The editor now shows the repaired account. Check it and save again.";

/** The tooltip on actions an audience refuses while it needs repair (spec UI 13). */
export const REPAIR_AUDIENCE_FIRST = 'Repair this audience first';
